<template>
    <div ref="timelineContainer" class="timeline-container"></div>
</template>

<script>
import 'ol/ol.css'
import 'ol-ext/dist/ol-ext.css'

import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import Feature from 'ol/Feature'
import { Point } from 'ol/geom'
import { Stroke, Style } from 'ol/style'
import Photo from 'ol-ext/style/Photo'
import Select from 'ol/interaction/Select'
import Timeline from 'ol-ext/control/Timeline'
import persianDate from 'persian-date';

import eventsData from '@/data/timeline-events.json'
import Overlay from "ol/Overlay";

export default {
    name: 'TimelineLikeExample',
    data() {
        return {
            map: this.$store.getters['map/getMap'],
            styleCache: Object.create(null),
            events: eventsData.map(e => ({
                ...e,
                date: new Date(e.date),
                endDate: new Date(e.endDate),
            })),
            eventsFeatures: null
        }
    },
    mounted() {
        this.initTimeline()
    },
    methods: {
        initTimeline() {
            const loader = this.$loading.show({
                loader: "dots",
            });
            setTimeout(function () {
                loader.hide();
            }, 1500);
            this.eventsFeatures = this.createFeatures();

            const vectorSource = new VectorSource({ features: this.eventsFeatures });
            const vectorLayer = new VectorLayer({
                source: vectorSource,
                style: this.markerStyle(false),
            });
            this.map.addLayer(vectorLayer);

            const minYear = 1910;
            const maxYear = 2029;
            const chunk = 30;
            const timelineFeatures = [];
            for (let y = minYear; y <= maxYear; y += chunk) {
                const startYear = y;
                const endYear = Math.min(y + chunk - 1, maxYear);
                timelineFeatures.push({
                    text: `${startYear}-${endYear}`,
                    date: new Date(`${startYear}-01-01`),
                    endDate: new Date(`${endYear}-12-31`),
                });
            }

            const eventTimelineFeatures = this.eventsFeatures;

            const allTimelineFeatures = [
                ...timelineFeatures,
                ...eventTimelineFeatures
            ];

            const timeline = new Timeline({
                className: 'custom-timeline',
                features: allTimelineFeatures,
                interval: chunk * 365 + 'd',
                graduation: 'month',
                zoomButton: false,
                minDate: new Date(`${minYear}-01-01`),
                maxDate: new Date(`${maxYear + 1}-01-01`),

                // 👇 Make getters robust to both types
                getHTML: (f) => (typeof f.get === 'function' ? (f.get('title') || f.get('text') || '') : (f.text || '')),
                getFeatureDate: (f) => (typeof f.get === 'function' ? f.get('date') : f.date),
                endFeatureDate: (f) => {
                    if (typeof f.get === 'function') return f.get('endDate') || f.get('date');
                    return f.endDate || f.date;
                },
            });

            this.map.addControl(timeline);

            const lastFeature = timelineFeatures.slice(-1)[0];
            if (lastFeature) {
                const start = lastFeature.date.getTime();
                const end = lastFeature.endDate.getTime();
                const mid = new Date((start + end) / 2);
                timeline.setDate(mid);
            }

            const select = new Select({
                hitTolerance: 5,
                style: this.markerStyle(true)
            });
            this.map.addInteraction(select);
            select.on('select', (e) => {
                const feature = e.selected[0];
                if (!feature) return;
                const coords = feature.getGeometry().getCoordinates();
                this.map.getView().animate({ center: coords, zoom: 13 });
                const date = feature.get('date');
                if (date) timeline.setDate(date);
                this.showOverlay(feature);
            });

            this.map.on('singleclick', (e) => {
                const feature = this.map.forEachFeatureAtPixel(e.pixel, (f) => f);
                if (!feature) {
                    this.closePopup();
                    return;
                }
            });

            timeline.on('select', (e) => {
                this.closePopup();
                select.getFeatures().clear();
                const sel = e.feature;
                if (!sel) return;

                const getProp = (obj, key) => (typeof obj.get === 'function' ? obj.get(key) : obj[key]);
                const geom = (typeof sel.getGeometry === 'function') ? sel.getGeometry() : sel.geometry;

                if (geom) {
                    const coords = geom.getCoordinates();
                    select.getFeatures().push(sel);
                    this.map.getView().animate({ center: coords, zoom: 13 });
                }

                const d1 = getProp(sel, 'date');
                const d2 = getProp(sel, 'endDate') || d1;
                if (d1) {
                    const mid = new Date((d1.getTime() + d2.getTime()) / 2);
                    timeline.setDate(mid);

                }

                if (typeof sel.get === 'function') {
                    this.showOverlay(sel);
                    return;
                }

                this.map.getView().animate({ center: [5938851.349645053, 3825520.3916165023], zoom: 4 });
            });

            timeline.on('scroll', (e) => {
                const start = e.dateStart;
                const end = e.dateEnd;
                vectorSource.getFeatures().forEach((f) => {
                    const fStart = f.get('date');
                    const fEnd = f.get('endDate') || fStart;
                    const visible = !(fEnd < start || fStart > end);
                    if (f.get('visible') !== visible) {
                        f.set('visible', visible);
                        f.changed();
                    }
                });
            });
        },
        createFeatures() {
            return this.events.map((event) => {
                return new Feature({
                    geometry: new Point(event.where),
                    where: event.where,
                    title: event.title,
                    date: event.date,
                    endDate: event.endDate,
                    description: event.description,
                    image: event.image,
                    visible: true,
                });
            });
        },
        markerStyle(select = false) {
            return (f) => {
                if (f.get('visible') === false) return [];

                const key = `${f.get('image') || ''}-${select ? '1' : '0'}`;
                if (!this.styleCache[key]) {
                    const img = new Photo({
                        src: f.get('image'),
                        radius: select ? 28 : 20,
                        crop: true,
                        kind: select ? 'anchored' : 'square',
                        shadow: select ? 10 : 5,
                        stroke: new Stroke({
                            width: select ? 3 : 2,
                            color: '#fff',
                        }),
                    });

                    this.styleCache[key] = new Style({
                        image: img,
                        zIndex: select ? 1 : 0,
                    });
                }

                return this.styleCache[key];
            };
        },
        showOverlay(feature) {
            this.closePopup();

            const container = document.createElement('div');
            container.className = 'ol-popup';
            container.style.background = '#fff';
            container.style.padding = '10px';
            container.style.borderRadius = '6px';
            container.style.boxShadow = '0 1px 4px rgba(0,0,0,0.2)';
            container.style.maxWidth = '400px';
            container.style.position = 'absolute';
            container.style.top = '25px';
            container.style.left = '50%';
            container.style.transform = 'translateX(-50%)';

            const title = document.createElement('h6');
            title.innerText = feature.get('title').toUpperCase();
            title.style.textAlign = 'center';
            title.style.fontWeight = 'bold';
            title.style.margin = '0 0 8px 0';

            const hr = document.createElement('hr');
            hr.style.width = "220px";
            hr.style.margin = "0 auto 8px auto";

            const description = document.createElement('p');
            description.innerText = `${feature.get('description')}`;
            description.style.margin = '0';
            description.style.textAlign = 'right';

            const time = document.createElement('p');
            time.innerText = `${new persianDate(feature.get('date')).format("L")}` +
                    ' - ' +
                    `${new persianDate(feature.get('endDate')).format("L")}`;
            time.style.margin = '6px 0 0 0';
            time.style.textAlign = 'center';

            container.appendChild(title);
            container.appendChild(hr);
            container.appendChild(description);
            container.appendChild(time);

            this.overlay = new Overlay({
                element: container,
                positioning: 'top-center',
                autoPan: true,
                autoPanMargin: 20,
                autoPanAnimation: { duration: 100 },
            });

            this.map.addOverlay(this.overlay);
            this.overlay.setPosition(feature.get('where'));
        },
        closePopup() {
            if (this.overlay) {
                this.map.removeOverlay(this.overlay);
                this.overlay = null;
            }
        }
    },
}
</script>

<style>
.custom-timeline {
    width: 100%;
    left: 50px !important;
}

.custom-timeline .ol-scroll {
    overflow-x: auto !important;
}

.custom-timeline .ol-feature {
    font-size: 14px !important;
    font-weight: bold !important;
    text-align: center !important;
}
</style>
